import { esperar as esperarPadrao } from '@/shared/utils/esperar';
import { ehUrlNavegavelSegura } from '@/shared/utils/urlSegura';
import type {
  ExportacaoPronta,
  MotivoDaFalhaDaExportacao,
  OpcoesDaEspera,
  StatusDaExportacao,
} from '@/shared/types/exportacaoTypes';

const INTERVALO_PADRAO_MS = 2000;
const LIMITE_PADRAO_MS = 180000;

export const MENSAGEM_FALHA_DA_EXPORTACAO = 'Erro ao gerar a exportação';
export const MENSAGEM_TEMPO_ESGOTADO_DA_EXPORTACAO =
  'Tempo esgotado ao aguardar a exportação. Tente novamente em instantes.';
export const MENSAGEM_URL_INVALIDA_DA_EXPORTACAO =
  'O arquivo exportado veio com um endereço inválido. Tente novamente.';

export class FalhaDaExportacao extends Error {
  readonly motivo: MotivoDaFalhaDaExportacao;

  constructor(mensagem: string, motivo: MotivoDaFalhaDaExportacao) {
    super(mensagem);
    this.name = 'FalhaDaExportacao';
    this.motivo = motivo;
  }
}

const tempoEsgotado = () =>
  new FalhaDaExportacao(MENSAGEM_TEMPO_ESGOTADO_DA_EXPORTACAO, 'tempo_esgotado');

/**
 * Uma consulta de status que não responde não pode segurar o export além do prazo: o `signal`
 * cancela a requisição quando o tempo restante acaba, e a corrida com o prazo encerra a espera
 * mesmo se quem consulta ignorar o `signal`.
 */
async function consultarDentroDoPrazo<T>(
  consultarStatus: (signal: AbortSignal) => Promise<T>,
  restanteMs: number,
): Promise<T> {
  const controle = new AbortController();
  const prazo = new Promise<never>((_, rejeitar) => {
    controle.signal.addEventListener('abort', () => rejeitar(tempoEsgotado()), { once: true });
  });
  const timer = setTimeout(() => controle.abort(), Math.max(0, restanteMs));
  try {
    return await Promise.race([consultarStatus(controle.signal), prazo]);
  } catch (erro) {
    if (controle.signal.aborted) throw tempoEsgotado();
    throw erro;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Consulta o status de uma exportação assíncrona até ela ficar pronta e devolve o último status
 * (com a URL assinada). Lança `FalhaDaExportacao` quando o job falha, o prazo esgota (inclusive
 * com uma consulta ainda sem resposta) ou a URL não é navegável, com a mensagem que a tela mostra;
 * a falha da própria consulta sobe como veio.
 *
 * `consultarStatus` recebe o `AbortSignal` da consulta, que expira no tempo restante do prazo:
 * repasse-o à requisição para ela não seguir viva depois da desistência.
 *
 * READY sem `signedUrl` continua esperando: o worker grava o status antes de a URL existir, e
 * abrir uma aba vazia nesse intervalo perderia o arquivo. READY com URL que não é http(s) é
 * recusado: os chamadores a passam a `window.open`, e `javascript:` ali executa na origem da página.
 */
export async function aguardarExportacaoPronta<T extends StatusDaExportacao>(
  consultarStatus: (signal: AbortSignal) => Promise<T>,
  {
    intervaloMs = INTERVALO_PADRAO_MS,
    limiteMs = LIMITE_PADRAO_MS,
    agora = Date.now,
    esperar = esperarPadrao,
  }: OpcoesDaEspera = {},
): Promise<ExportacaoPronta<T>> {
  const inicio = agora();
  const restanteMs = () => limiteMs - (agora() - inicio);

  // O prazo é conferido antes de cada consulta, e a espera não passa dele (CodeRabbit 4461159991):
  // uma consulta iniciada sem tempo restante podia devolver READY antes do timer de 0 ms e ser
  // aceita depois do prazo, e a espera fixa atrasava a desistência em até um intervalo.
  while (true) {
    const antesDaConsulta = restanteMs();
    if (antesDaConsulta <= 0) throw tempoEsgotado();
    const status = await consultarDentroDoPrazo(consultarStatus, antesDaConsulta);

    if (status.status === 'READY' && status.signedUrl) {
      if (!ehUrlNavegavelSegura(status.signedUrl)) {
        throw new FalhaDaExportacao(MENSAGEM_URL_INVALIDA_DA_EXPORTACAO, 'url_invalida');
      }
      return { ...status, signedUrl: status.signedUrl.trim() };
    }

    if (status.status === 'FAILED') {
      throw new FalhaDaExportacao(status.error ?? MENSAGEM_FALHA_DA_EXPORTACAO, 'falhou_no_worker');
    }

    const antesDaEspera = restanteMs();
    if (antesDaEspera <= 0) throw tempoEsgotado();

    await esperar(Math.min(intervaloMs, antesDaEspera));
  }
}
